import {computed, inject, Injectable, signal} from "@angular/core";
import {
  catchError,
  concatMap,
  EMPTY,
  exhaustMap,
  from,
  Observable,
  Subject,
  tap,
} from "rxjs";
import {
  transformWorkstationForDatabase,
  Workstation,
} from "../../../shared/interfaces/workstation";
import {ApiService, Tables} from "src/app/services/api.service";
import {SelectionService} from "src/app/services/selection.service";
import {takeUntilDestroyed, toSignal} from "@angular/core/rxjs-interop";

interface WorkstationState {
  workstations: Workstation[];
  filteredWorkstations: Workstation[];
  workstationBeingEdited: Workstation | null;
}

@Injectable({
  providedIn: "root",
})
export class WorkstationService {
  apiService = inject(ApiService);
  selectionService = inject(SelectionService);

  // sources
  private currentProductionLine$ = toSignal(
    this.selectionService.currentProductionLine,
    {initialValue: ""}
  );
  workstations$: Observable<Workstation[]> =
    this.apiService.getWorkstationsAdmin();

  add$ = new Subject<Workstation>();
  update$ = new Subject<Workstation>();
  delete$ = new Subject<string>();
  workstationBeingEdited$ = new Subject<Workstation>();

  // state
  private state = signal<WorkstationState>({
    workstations: [],
    filteredWorkstations: [],
    workstationBeingEdited: null,
  });

  // selectors
  workstations = computed(() => this.state().workstations);
  filteredWorkstations = computed(() =>
    this.currentProductionLine$()
      ? this.state()
        .workstations.filter(
          (ws) => ws.production_line.id === this.currentProductionLine$()
        )
        .sort((a, b) => a.position - b.position)
      : this.state().workstations
  );
  workstationBeingEdited = computed(() => this.state().workstationBeingEdited);

  constructor() {
    // reducers

    this.workstations$
      .pipe(takeUntilDestroyed())
      .subscribe((workstations: Workstation[]) =>
        this.state.update((state) => ({
          ...state,
          workstations,
        }))
      );

    this.add$
      .pipe(
        takeUntilDestroyed(),
        exhaustMap((workstation) => this.addWorkstation(workstation)),
        catchError((error) => {
          console.error("Error adding workstation", error);
          return EMPTY;
        })
      )
      .subscribe((newWorkstation) => {
        this.state.update((state) => ({
          ...state,
          workstations: [...state.workstations, newWorkstation],
        }));
      });

    this.update$
      .pipe(
        takeUntilDestroyed(),
        concatMap((workstation) => this.updateWorkstation(workstation)),
        catchError((error) => {
          console.error("Error updating workstation", error);
          return EMPTY;
        })
      )
      .subscribe((updatedWorkstation) => {
        this.state.update((state) => ({
          ...state,
          workstations: state.workstations.map((workstation) =>
            workstation.id === updatedWorkstation.id
              ? updatedWorkstation
              : workstation
          ),
        }));
      });

    this.delete$
      .pipe(
        takeUntilDestroyed(),
        concatMap((id) =>
          from(this.deleteWorkstation(id)).pipe(
            tap(() => {
              this.state.update((state) => ({
                ...state,
                workstations: state.workstations.filter(
                  (workstation) => workstation.id !== id
                ),
              }));
            }),
            catchError((error) => {
              console.error("Error deleting workstation:", error);
              return EMPTY;
            })
          )
        )
      )
      .subscribe();

    this.workstationBeingEdited$
      .pipe(takeUntilDestroyed())
      .subscribe((workstation) =>
        this.state.update((state) => ({
          ...state,
          workstationBeingEdited: workstation,
        }))
      );
  }

  private addWorkstation(workstation: Workstation) {
    return this.apiService.addWorkstation(
      Tables.Workplace,
      transformWorkstationForDatabase(workstation)
    );
  }

  private updateWorkstation(workstation: Workstation) {
    return this.apiService.updateWorkstation(
      Tables.Workplace,
      transformWorkstationForDatabase(workstation)
    );
  }

  private deleteWorkstation(id: string) {
    return this.apiService.deleteItem(Tables.Workplace, id);
  }
}
